import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { HeaderBar } from "../components/common";
import NavBar from "../components/layout/NavBar";
import { useAuth } from "../components/auth/AuthContext";
import { apiEndpoint } from "../utils/env";
import "./Upload.css";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { createDatabase, addDatabaseIndexes } from "../api/adminApi";
import BulkSessionsSection from "./uploadSections/BulkSessionsSection";
import { formatPath } from "../utils/pathFormat";

// Renders the drop-folder path with a styled tooltip that appears on hover
// ONLY when the path is actually truncated (overflowing its cell).
function PathCell({ text, placeholder }) {
  const ref = useRef(null);
  const [trimmed, setTrimmed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const check = () => setTrimmed(el.scrollWidth > el.clientWidth + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text]);

  return (
    <div className={`df-path-cell-wrap${trimmed ? " df-path-trimmed" : ""}`}>
      <div ref={ref} className="df-path-cell">
        {text ? text : <span className="df-no-path">{placeholder}</span>}
      </div>
      {trimmed && text ? (
        <span className="df-path-tooltip" role="tooltip">{text}</span>
      ) : null}
    </div>
  );
}

const DB_DRIVER_TABS = [
  { id: "none", label: "None" },
  { id: "afp",  label: "Driver AFP" },
  { id: "meta", label: "JSON / CSV" },
];

const DB_CONTROL_FIELDS = new Set(["pagecount", "documentname"]);

function parseDriverFileClientSide(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file."));
    reader.onload = (e) => {
      const text = (e.target.result || "").trim();
      try {
        if (file.name.toLowerCase().endsWith(".json")) {
          const parsed = JSON.parse(text);
          let keys = [];
          if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === "object") {
            keys = Object.keys(parsed[0]);
          } else if (typeof parsed === "object" && parsed !== null) {
            keys = Object.keys(parsed);
          }
          keys = keys.filter((k) => !DB_CONTROL_FIELDS.has(k.toLowerCase()));
          if (keys.length === 0) throw new Error("No index fields found in JSON file.");
          resolve(keys.map((k) => ({ name: k, sampleValues: [] })));
        } else {
          const firstLine = text.split(/\r?\n/).find((l) => l.trim());
          if (!firstLine) throw new Error("CSV file appears to be empty.");
          const headers = firstLine
            .split(",")
            .map((h) => h.trim().replace(/^["']|["']$/g, ""))
            .filter((h) => h && !DB_CONTROL_FIELDS.has(h.toLowerCase()));
          if (headers.length === 0) throw new Error("No index columns found in CSV.");
          resolve(headers.map((h) => ({ name: h, sampleValues: [] })));
        }
      } catch (err) {
        reject(err);
      }
    };
    reader.readAsText(file);
  });
}

const ENCODING_PRESETS = [
  { value: "utf-8",        name: "UTF-8",         desc: "Universal — recommended for all languages" },
  { value: "utf-16le",     name: "UTF-16 LE",      desc: "Unicode Little Endian — Windows default" },
  { value: "utf-16be",     name: "UTF-16 BE",      desc: "Unicode Big Endian" },
  { value: "ascii",        name: "ASCII",          desc: "7-bit English only" },
  { value: "iso-8859-1",   name: "ISO-8859-1",     desc: "Latin-1 — Western European (English, French, German, Spanish)" },
  { value: "iso-8859-2",   name: "ISO-8859-2",     desc: "Latin-2 — Central European (Polish, Czech, Slovak, Hungarian)" },
  { value: "iso-8859-9",   name: "ISO-8859-9",     desc: "Latin-5 — Turkish" },
  { value: "iso-8859-15",  name: "ISO-8859-15",    desc: "Latin-9 — Western European with € euro sign" },
  { value: "iso-8859-5",   name: "ISO-8859-5",     desc: "Cyrillic — Russian, Bulgarian, Serbian" },
  { value: "iso-8859-7",   name: "ISO-8859-7",     desc: "Greek" },
  { value: "iso-8859-8",   name: "ISO-8859-8",     desc: "Hebrew" },
  { value: "iso-8859-6",   name: "ISO-8859-6",     desc: "Arabic" },
  { value: "windows-1250", name: "Windows-1250",   desc: "Central European Windows (Polish, Czech, Romanian)" },
  { value: "windows-1251", name: "Windows-1251",   desc: "Cyrillic Windows (Russian, Ukrainian, Bulgarian)" },
  { value: "windows-1252", name: "Windows-1252",   desc: "Western European Windows (English, French, German)" },
  { value: "windows-1253", name: "Windows-1253",   desc: "Greek Windows" },
  { value: "windows-1254", name: "Windows-1254",   desc: "Turkish Windows" },
  { value: "windows-1256", name: "Windows-1256",   desc: "Arabic Windows" },
  { value: "shift-jis",    name: "Shift-JIS",      desc: "Japanese — Windows/macOS legacy" },
  { value: "euc-jp",       name: "EUC-JP",         desc: "Japanese — Unix/Linux" },
  { value: "euc-kr",       name: "EUC-KR",         desc: "Korean" },
  { value: "gb2312",       name: "GB2312",         desc: "Chinese Simplified — mainland China" },
  { value: "gbk",          name: "GBK",            desc: "Chinese Simplified extended — Windows China" },
  { value: "big5",         name: "Big5",           desc: "Chinese Traditional — Taiwan, Hong Kong" },
];

export default function Upload() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const location = useLocation();

  // Section navigation
  const [section, setSection] = useState("drop");

  useEffect(() => {
    if (location.state?.section) {
      setSection(location.state.section);
    }
  }, [location.state]);

  // Drop folders & history
  const [dropFolders, setDropFolders] = useState([]);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [queue, setQueue] = useState([]); // [{id,name,size,progress,status}]

  const fileInputRef = useRef(null);

  // ── Upload-to-database modal state ──────────────────────────────────────
  const [dbUploadModal, setDbUploadModal] = useState(false);
  const [dbUploadMode, setDbUploadMode] = useState("existing"); // "existing" | "new"
  const [dbUploadName, setDbUploadName] = useState("");
  const [dbUploadExisting, setDbUploadExisting] = useState("");
  const [dbUploadAllDbs, setDbUploadAllDbs] = useState([]);
  const [dbUploadFiles, setDbUploadFiles] = useState([]);
  const [dbUploadBusy, setDbUploadBusy] = useState(false);
  const [dbUploadError, setDbUploadError] = useState("");
  const [dbUploadResult, setDbUploadResult] = useState(null);

  // Step 2: index definition (only shown when a new DB was just created with a driver)
  const [dbUploadStep, setDbUploadStep] = useState(1); // 1 | 2 | "done"
  const [dbUploadDbInfoId, setDbUploadDbInfoId] = useState(null);
  const [dbUploadCreatedName, setDbUploadCreatedName] = useState("");

  // Driver file state (new DB creation — mirrors CreateDatabaseModal)
  const [dbUploadDriverTab, setDbUploadDriverTab] = useState("none"); // "none" | "afp" | "meta"
  const [dbUploadDriverAfp, setDbUploadDriverAfp] = useState(null);
  const [dbUploadDriverMeta, setDbUploadDriverMeta] = useState(null);

  // Unified field selection for step 2 (mirrors CreateDatabaseModal step 2)
  const [dbUploadFields, setDbUploadFields] = useState([]);
  const [dbUploadSelected, setDbUploadSelected] = useState(new Set());
  const [dbUploadRenames, setDbUploadRenames] = useState({});
  const [dbUploadFieldBusy, setDbUploadFieldBusy] = useState(false);
  const [dbUploadFieldError, setDbUploadFieldError] = useState("");
  const [dbUploadIndexCount, setDbUploadIndexCount] = useState(0);

  const driverAfpRef  = useRef(null);
  const driverMetaRef = useRef(null);

  // ── Drop folder CRUD modal state ─────────────────────────────────────────
  const [dbInfos, setDbInfos] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // create | edit | delete
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    dbInfoId: "",
    storageDrive: "",
    storageFolder: "",
    processLocation: "",
    errorLocation: "",
    defaultEncoding: "",
    defaultFolderId: "",
  });
  const [formError, setFormError] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [encodingPanelOpen, setEncodingPanelOpen] = useState(false);
  const [copiedFolderSettings, setCopiedFolderSettings] = useState(null);
  const [copyFlash, setCopyFlash] = useState(false);
  const [dfSearch, setDfSearch] = useState("");

  // ── Folder picker state ──────────────────────────────────────────────────
  const [picker, setPicker] = useState({ open: false, target: null, path: "", parent: null, entries: [], loading: false, error: "" });

  const headers = useMemo(
    () => ({
      Authorization: token ? `Bearer ${token}` : undefined,
    }),
    [token]
  );

  // ── Folder picker helpers ─────────────────────────────────────────────────
  const navigatePicker = useCallback(async (path, fallbackToRoot = false) => {
    setPicker(p => ({ ...p, loading: true, error: "" }));
    try {
      const url = apiEndpoint(`/api/Upload/BrowseFolders?path=${encodeURIComponent(path)}`);
      const resp = await fetch(url, { headers });
      if (!resp.ok) throw new Error("failed");
      const data = await resp.json();
      setPicker(p => ({ ...p, path: data.path, parent: data.parent ?? null, entries: data.entries, loading: false }));
    } catch {
      if (fallbackToRoot) {
        // Path invalid — start from default root
        try {
          const url = apiEndpoint("/api/Upload/BrowseFolders?path=");
          const resp = await fetch(url, { headers });
          if (!resp.ok) throw new Error("failed");
          const data = await resp.json();
          setPicker(p => ({ ...p, path: data.path, parent: data.parent ?? null, entries: data.entries, loading: false }));
        } catch {
          setPicker(p => ({ ...p, loading: false, error: "Could not load folder." }));
        }
      } else {
        setPicker(p => ({ ...p, loading: false, error: "Could not load folder." }));
      }
    }
  }, [headers]);

  const copyFolderSettings = useCallback(() => {
    const { storageDrive, storageFolder, processLocation, errorLocation, defaultEncoding } = form;
    setCopiedFolderSettings({ storageDrive, storageFolder, processLocation, errorLocation, defaultEncoding });
    setCopyFlash(true);
    setTimeout(() => setCopyFlash(false), 1500);
  }, [form]);

  const pasteFolderSettings = useCallback(() => {
    if (!copiedFolderSettings) return;
    setForm(f => ({ ...f, ...copiedFolderSettings }));
  }, [copiedFolderSettings]);

  const openPicker = useCallback((target, currentValue) => {
    const startPath = currentValue && currentValue.trim() ? currentValue.trim() : "";
    setPicker({ open: true, target, path: startPath, parent: null, entries: [], loading: true, error: "" });
    navigatePicker(startPath, true);
  }, [navigatePicker]);

  // ── Loaders ──────────────────────────────────────────────────────────────
  const loadDropFolders = useCallback(async () => {
    try {
      const url = apiEndpoint("/api/Upload/DropFolders");
      const resp = await fetch(url, { headers });
      if (!resp.ok) throw new Error("no-endpoint");
      const data = await resp.json();
      if (Array.isArray(data)) { setDropFolders(data); return; }
      throw new Error("bad-shape");
    } catch {
      setDropFolders([]);
    }
  }, [headers]);

  const loadUploadHistory = useCallback(async () => {
    try {
      const url = apiEndpoint("/api/Upload/UploadHistory");
      const resp = await fetch(url, { headers });
      if (!resp.ok) throw new Error("no-endpoint");
      const data = await resp.json();
      if (Array.isArray(data)) { setHistory(data); return; }
      throw new Error("bad-shape");
    } catch {
      setHistory([]);
    }
  }, [headers]);

  const loadDbInfos = useCallback(async () => {
    try {
      // Use AllDatabases (same source as the upload modal) so newly created
      // databases are always visible. Normalize to {id, name} for the dropdown.
      const url = apiEndpoint("/api/Upload/AllDatabases");
      const resp = await fetch(url, { headers });
      if (!resp.ok) throw new Error("no-endpoint");
      const data = await resp.json();
      if (Array.isArray(data))
        setDbInfos(data.map((d) => ({ id: d.id, name: d.displayName || d.dbName })));
    } catch {
      setDbInfos([]);
    }
  }, [headers]);

  useEffect(() => {
    if (section === "drop") { loadDropFolders(); loadDbInfos(); }
    if (section === "history") loadUploadHistory();
  }, [section, loadDropFolders, loadUploadHistory, loadDbInfos]);

  // Auto-select first DB when opening create modal
  useEffect(() => {
    if (modalOpen && modalMode === "create" && !form.dbInfoId && dbInfos.length > 0)
      setForm((f) => ({ ...f, dbInfoId: dbInfos[0].id }));
  }, [modalOpen, modalMode, dbInfos, form.dbInfoId]);

  // ── DB upload modal callbacks ─────────────────────────────────────────────
  const loadAllDatabases = useCallback(async () => {
    try {
      const url = apiEndpoint("/api/Upload/AllDatabases");
      const resp = await fetch(url, { headers });
      if (!resp.ok) throw new Error("no-endpoint");
      const data = await resp.json();
      if (Array.isArray(data)) setDbUploadAllDbs(data);
    } catch {
      setDbUploadAllDbs([]);
    }
  }, [headers]);

  const openDbUploadModal = useCallback(
    (files) => {
      if (!files || files.length === 0) return;
      setDbUploadFiles(Array.from(files));
      setDbUploadMode("existing");
      setDbUploadExisting("");
      setDbUploadName("");
      setDbUploadError("");
      setDbUploadResult(null);
      setDbUploadStep(1);
      setDbUploadDbInfoId(null);
      setDbUploadCreatedName("");
      setDbUploadDriverTab("none");
      setDbUploadDriverAfp(null);
      setDbUploadDriverMeta(null);
      setDbUploadFields([]);
      setDbUploadSelected(new Set());
      setDbUploadRenames({});
      setDbUploadFieldBusy(false);
      setDbUploadFieldError("");
      setDbUploadIndexCount(0);
      setDbUploadModal(true);
      loadAllDatabases();
    },
    [loadAllDatabases]
  );

  // Upload files to a (possibly just-created) database by name
  const uploadFilesToDb = useCallback(async (dbName) => {
    const formData = new FormData();
    dbUploadFiles.forEach((f) => formData.append("files", f));
    formData.append("databaseName", dbName);
    formData.append("isNewDatabase", "false");
    const url = apiEndpoint("/api/Upload/UploadToDatabase");
    const resp = await fetch(url, {
      method: "POST",
      headers: headers.Authorization ? { Authorization: headers.Authorization } : {},
      body: formData,
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err?.message || `HTTP ${resp.status}`);
    }
    return await resp.json();
  }, [dbUploadFiles, headers]);

  const submitUploadToDatabase = useCallback(async () => {
    const dbName = dbUploadMode === "new" ? dbUploadName.trim() : dbUploadExisting;
    if (!dbName) {
      setDbUploadError(
        dbUploadMode === "new" ? "Enter a name for the new database." : "Select an existing database."
      );
      return;
    }
    if (dbUploadFiles.length === 0) { setDbUploadError("No files to upload."); return; }

    setDbUploadBusy(true);
    setDbUploadError("");
    setDbUploadResult(null);

    try {
      if (dbUploadMode === "new") {
        // Step A: create the database first
        const createResult = await createDatabase(token, dbName);
        if (!createResult?.success) {
          setDbUploadError(createResult?.message || "Failed to create database.");
          return;
        }
        const dbId = createResult.dbId ?? createResult.id ?? null;
        setDbUploadDbInfoId(dbId);
        setDbUploadCreatedName(dbName);

        const driverFile = dbUploadDriverTab === "afp" ? dbUploadDriverAfp
                         : dbUploadDriverTab === "meta" ? dbUploadDriverMeta
                         : null;

        if (driverFile) {
          // Step B: detect fields from driver, then show step 2
          setDbUploadBusy(false);
          setDbUploadFieldBusy(true);
          setDbUploadStep(2);
          try {
            let detectedFields = [];
            if (dbUploadDriverTab === "afp") {
              const fd = new FormData();
              fd.append("file", driverFile);
              const url = apiEndpoint("/api/Upload/ParseIndexHints");
              const resp = await fetch(url, {
                method: "POST",
                headers: headers.Authorization ? { Authorization: headers.Authorization } : {},
                body: fd,
              });
              if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err?.message || `HTTP ${resp.status}`);
              }
              const data = await resp.json();
              if (data.fields && data.fields.length > 0) {
                detectedFields = data.fields;
              } else {
                setDbUploadFieldError(
                  data.note ||
                  (data.tleRecordCount === 0
                    ? "No TLE records found in this AFP. You can add indexes from the Admin panel."
                    : `${data.tleRecordCount} TLE record(s) found but no field names extracted.`)
                );
              }
            } else {
              // meta (JSON / CSV) — parse client-side
              detectedFields = await parseDriverFileClientSide(driverFile);
            }
            if (detectedFields.length > 0) {
              setDbUploadFields(detectedFields);
              setDbUploadSelected(new Set(detectedFields.map((f) => f.name)));
            }
          } catch (e) {
            setDbUploadFieldError(e.message || "Field detection failed.");
          } finally {
            setDbUploadFieldBusy(false);
          }
          return; // step 2 shown; file upload happens after indexes chosen
        } else {
          // No driver — upload files immediately and finish
          const uploadResult = await uploadFilesToDb(dbName);
          setDbUploadResult(uploadResult);
          setDbUploadStep("done");
        }
      } else {
        // Existing database — upload directly
        const formData = new FormData();
        dbUploadFiles.forEach((f) => formData.append("files", f));
        formData.append("databaseName", dbName);
        formData.append("isNewDatabase", "false");
        const url = apiEndpoint("/api/Upload/UploadToDatabase");
        const resp = await fetch(url, {
          method: "POST",
          headers: headers.Authorization ? { Authorization: headers.Authorization } : {},
          body: formData,
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err?.message || `HTTP ${resp.status}`);
        }
        const data = await resp.json();
        setDbUploadResult(data);
        setDbUploadStep("done");
      }
    } catch (e) {
      setDbUploadError(e.message || "Upload failed.");
    } finally {
      setDbUploadBusy(false);
    }
  }, [dbUploadMode, dbUploadName, dbUploadExisting, dbUploadFiles, dbUploadDriverTab,
      dbUploadDriverAfp, dbUploadDriverMeta, headers, token, uploadFilesToDb]);

  // Step 2: add selected indexes to the newly created database
  const handleDbUploadAddIndexes = useCallback(async () => {
    if (!dbUploadDbInfoId) { setDbUploadFieldError("Database ID unavailable. Please close and retry."); return; }
    const chosen = dbUploadFields.filter((f) => dbUploadSelected.has(f.name));
    if (chosen.length === 0) { setDbUploadFieldError("Select at least one field, or click Skip."); return; }
    setDbUploadFieldBusy(true);
    setDbUploadFieldError("");
    try {
      const result = await addDatabaseIndexes(token, dbUploadDbInfoId, chosen.map((f, i) => ({
        fieldName: f.name.replace(/[^A-Za-z0-9_]/g, "_"),
        fieldDisplayName: (dbUploadRenames[f.name] || f.name).trim() || f.name,
        dataType: "string",
        length: 200,
        isShownOnReport: true,
        isActive: true,
        isDeletable: true,
        fieldOrder: i,
      })));
      if (result?.success === false) {
        setDbUploadFieldError(result.message || "Failed to add indexes.");
        return;
      }
      setDbUploadIndexCount(chosen.length);
      // After indexes, upload the files
      const uploadResult = await uploadFilesToDb(dbUploadCreatedName);
      setDbUploadResult(uploadResult);
      setDbUploadStep("done");
    } catch (e) {
      setDbUploadFieldError(e?.message || "Failed.");
    } finally {
      setDbUploadFieldBusy(false);
    }
  }, [dbUploadDbInfoId, dbUploadFields, dbUploadSelected, dbUploadRenames,
      dbUploadCreatedName, token, uploadFilesToDb]);

  const handleDbUploadSkipIndexes = useCallback(async () => {
    setDbUploadFieldBusy(true);
    setDbUploadFieldError("");
    try {
      const uploadResult = await uploadFilesToDb(dbUploadCreatedName);
      setDbUploadResult(uploadResult);
      setDbUploadIndexCount(0);
      setDbUploadStep("done");
    } catch (e) {
      setDbUploadFieldError(e?.message || "Upload failed after skipping indexes.");
    } finally {
      setDbUploadFieldBusy(false);
    }
  }, [dbUploadCreatedName, uploadFilesToDb]
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="app-root">
      <div className="nav-strip">
        <NavBar />
      </div>
      <HeaderBar />

      <div className="main-layout main-layout--no-detail">
        {/* Left navigation */}
        <nav className="side-panel dbx-nav">
          <div className="dbx-section">
            <div className="dbx-quick-hint">Files</div>
            <div
              className={`dbx-nav-item ${section === "files" ? "active" : ""}`}
              onClick={() => setSection("files")}
            >
              <span className="dbx-nav-icon">⬆️</span>
              Upload
            </div>
            <div
              className={`dbx-nav-item ${section === "drop" ? "active" : ""}`}
              onClick={() => setSection("drop")}
            >
              <span className="dbx-nav-icon">📂</span>
              Drop folders
            </div>
            <div
              className={`dbx-nav-item ${section === "history" ? "active" : ""}`}
              onClick={() => setSection("history")}
            >
              <span className="dbx-nav-icon">📋</span>
              Past uploads
            </div>
            <div
              className={`dbx-nav-item ${section === "sessions" ? "active" : ""}`}
              onClick={() => setSection("sessions")}
            >
              <span className="dbx-nav-icon">📦</span>
              {t("upload.sessions.navLabel", { defaultValue: "Bulk sessions" })}
            </div>
          </div>
        </nav>

        {/* Main content */}
        <main
          className="center-panel dbx-content"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const files = Array.from(e.dataTransfer.files || []);
            openDbUploadModal(files);
          }}
        >
          {/* ── Section: Upload ── */}
          {section === "files" && (
            <div className="dropzone-wrap">
              <div
                className="dropzone-enhanced"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const files = Array.from(e.dataTransfer.files || []);
                  openDbUploadModal(files);
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="dz-icon">☁️</div>
                <div className="dz-title">Drop files here to upload</div>
                <div className="dz-sub">Select a database and upload AFP, PDF or other document files</div>
                <div className="dz-formats">
                  {["AFP", "PDF", "TXT", "CSV", "XML", "JSON"].map((fmt) => (
                    <span key={fmt} className="dz-format-chip">{fmt}</span>
                  ))}
                </div>
                <div className="dz-or">— or —</div>
                <button
                  className="dz-btn"
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                >
                  Choose files
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length) openDbUploadModal(files);
                  e.target.value = "";
                }}
              />
            </div>
          )}

          {/* ── Section: Drop folders ── */}
          {section === "drop" && (
            <div className="df-panel">
              {/* Header */}
              <div className="df-header">
                <div className="df-title">Drop folders</div>
                <div className="df-stats-pill">
                  {dropFolders.length} of {dbInfos.length} configured
                </div>
                <input
                  className="df-search"
                  placeholder="Search databases…"
                  value={dfSearch}
                  onChange={(e) => setDfSearch(e.target.value)}
                />
                <button
                  className="df-refresh-btn"
                  title="Refresh"
                  onClick={() => { loadDropFolders(); loadDbInfos(); }}
                >
                  ⟲
                </button>
              </div>

              {/* Column headings */}
              <div className="df-thead">
                <div>Database</div>
                <div>Drop folder path</div>
                <div>Status</div>
                <div>Last activity</div>
                <div style={{ textAlign: "right" }}>Actions</div>
              </div>

              {/* Rows — scrollable body so long lists fit within the panel */}
              <div className="df-body">
              {(() => {
                const filtered = dfSearch
                  ? dbInfos.filter((db) =>
                      db.name.toLowerCase().includes(dfSearch.toLowerCase())
                    )
                  : dbInfos;

                if (filtered.length === 0) {
                  return (
                    <div className="df-empty">
                      <div className="df-empty-icon">
                        {dbInfos.length === 0 ? "🗄️" : "🔍"}
                      </div>
                      <div className="df-empty-text">
                        {dbInfos.length === 0
                          ? "No databases found"
                          : `No databases match "${dfSearch}"`}
                      </div>
                    </div>
                  );
                }

                return filtered.map((db) => {
                  const cfg = dropFolders.find(
                    (d) => String(d.dbInfoId) === String(db.id)
                  );
                  return (
                    <div
                      key={db.id}
                      className={`df-row ${cfg ? "df-configured" : "df-unconfigured"}`}
                    >
                      {/* Database name */}
                      <div className="df-db-cell">
                        <span className="df-db-icon">🗄️</span>
                        <div className="df-db-name" title={db.name}>{db.name}</div>
                      </div>

                      {/* Path */}
                      <PathCell
                        text={cfg ? formatPath(cfg.storageFolder || cfg.path || "") : ""}
                        placeholder={cfg ? "—" : "Not configured"}
                      />

                      {/* Status badge */}
                      <div>
                        {cfg
                          ? <span className="df-badge df-badge-active">Active</span>
                          : <span className="df-badge df-badge-inactive">Not set up</span>}
                      </div>

                      {/* Last activity */}
                      <div className="df-last-updated">
                        {cfg?.updatedAt
                          ? new Date(cfg.updatedAt).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })
                          : ""}
                      </div>

                      {/* Actions */}
                      <div className="df-actions-cell">
                        {cfg ? (
                          <>
                            <button
                              className="btn-edit"
                              title={t("upload.editTitle", { defaultValue: "Edit" })}
                              onClick={() => {
                                setModalMode("edit");
                                setEditing(cfg);
                                setAdvancedOpen(true);
                                setForm({
                                  dbInfoId: cfg.dbInfoId || "",
                                  storageDrive: formatPath(cfg.storageDrive || ""),
                                  storageFolder: formatPath(cfg.storageFolder || ""),
                                  processLocation: formatPath(cfg.processLocation || ""),
                                  errorLocation: formatPath(cfg.errorLocation || ""),
                                  defaultEncoding: cfg.defaultEncoding || "",
                                  packageSize: cfg.packageSize || "",
                                  defaultFolderId: cfg.defaultFolderId || "",
                                });
                                setFormError("");
                                setModalOpen(true);
                                loadDbInfos();
                              }}
                            >
                              Edit
                            </button>
                            <button
                              className="btn-delete"
                              title={t("upload.deleteTitle", { defaultValue: "Delete" })}
                              onClick={() => {
                                setModalMode("delete");
                                setEditing(cfg);
                                setFormError("");
                                setModalOpen(true);
                              }}
                            >
                              Delete
                            </button>
                          </>
                        ) : (
                          <button
                            className="btn-setup"
                            title="Configure drop folder"
                            onClick={() => {
                              setModalMode("create");
                              setEditing(null);
                              setAdvancedOpen(false);
                              setForm({
                                dbInfoId: String(db.id),
                                storageDrive: "",
                                storageFolder: "",
                                processLocation: "",
                                errorLocation: "",
                                defaultEncoding: "",
                                packageSize: "",
                                defaultFolderId: "",
                              });
                              setFormError("");
                              setModalOpen(true);
                              loadDbInfos();
                            }}
                          >
                            + Set up
                          </button>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
              </div>
            </div>
          )}

          {/* ── Section: Past uploads ── */}
          {section === "history" && (
            <div className="dbx-files">
              <div className="dbx-files-header">
                <div className="left">
                  <div className="dbx-row-title">Past uploads</div>
                </div>
                <div className="right">
                  <button className="mini-btn" onClick={() => loadUploadHistory()} disabled={busy}>
                    ⟲
                  </button>
                </div>
              </div>
              <div className="dbx-table">
                <div className="thead">
                  <div>File</div>
                  <div>Uploaded by</div>
                  <div>Date</div>
                </div>
                <div className="tbody">
                  {history.map((h) => (
                    <div key={h.id} className="trow">
                      <div className="name">
                        <span className="icon">📄</span>
                        {h.name}
                      </div>
                      <div className="access">{h.uploadedBy || "--"}</div>
                      <div className="modified">
                        {h.uploadedAt ? new Date(h.uploadedAt).toLocaleString() : "--"}
                      </div>
                    </div>
                  ))}
                  {history.length === 0 && (
                    <div className="trow empty">
                      <div className="name">No uploads yet</div>
                      <div /><div />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Section: Bulk upload sessions ── */}
          {section === "sessions" && (
            <BulkSessionsSection token={token} />
          )}

          {/* Upload queue dock */}
          {queue.length > 0 && (
            <div className="uploads uploads-dock">
              <div className="uploads-title">Recent uploads</div>
              <div className="uploads-list">
                {queue.map((u) => (
                  <div key={u.id} className={`upload-row ${u.status}`}>
                    <div className="u-name" title={u.name}>{u.name}</div>
                    <div className="u-size">{Math.ceil(u.size / 1024)} KB</div>
                    <div className="u-status">{u.status}</div>
                    <div className="u-progress">
                      <span style={{ width: `${u.progress}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* ── Upload-to-database modal ── */}
      {dbUploadModal && (
        <div
          className="modal-overlay"
          onClick={() => { if (!dbUploadBusy) setDbUploadModal(false); }}
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ minWidth: 440 }}
          >
            <div className="modal-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span>
                {dbUploadStep === 2
                  ? "⊞ Index Fields"
                  : dbUploadStep === "done"
                    ? "✓ Done"
                    : "📂 Choose destination database"}
              </span>
              <button
                className="drawer-close-btn"
                onClick={() => { if (!dbUploadBusy) setDbUploadModal(false); }}
                aria-label="Close"
                title="Close"
                disabled={dbUploadBusy}
              >
                <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
                  <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">

              {/* ── Step: done ── */}
              {dbUploadStep === "done" ? (
                <div style={{ textAlign: "center", padding: "12px 0" }}>
                  <div className="dbModalIcon" style={{ color: "#16a34a", fontSize: 28, marginBottom: 6 }}>✓</div>
                  <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>
                    {dbUploadCreatedName
                      ? `Database "${dbUploadCreatedName}" ready!`
                      : "Upload complete!"}
                  </div>
                  {dbUploadIndexCount > 0 && (
                    <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
                      {dbUploadIndexCount} index field(s) added.
                    </div>
                  )}
                  <div className="muted" style={{ marginTop: 6 }}>
                    {dbUploadResult?.uploads?.map((u, i) => (
                      <div key={i} style={{ marginTop: 4 }}>
                        <strong>{u.name}</strong>
                        {u.type === "AFP" && <span> → Batch process #{u.batchProcessId} queued</span>}
                        {u.type === "ManualUpload" && <span> → Uploaded (record #{u.storageInfoId})</span>}
                        {u.type === "IndexFile" && <span> → Index file saved</span>}
                      </div>
                    ))}
                  </div>
                </div>

              ) : dbUploadStep === 2 ? (
                /* ── Step 2: Field selection — mirrors CreateDatabaseModal step 2 ── */
                <>
                  <div className="dbModalDesc" style={{ marginBottom: 14 }}>
                    Database <strong>{dbUploadCreatedName}</strong> created.
                    Select the fields to add as index columns.
                  </div>

                  {dbUploadFieldBusy && (
                    <div style={{ color: "#555", fontSize: 13, marginBottom: 12 }}>
                      {dbUploadDriverTab === "afp" ? "Scanning AFP for TLE fields…" : "Parsing metadata file…"}
                    </div>
                  )}
                  {dbUploadFieldError && (
                    <div className="createDbFieldErr" style={{ marginBottom: 10 }}>{dbUploadFieldError}</div>
                  )}

                  {!dbUploadFieldBusy && dbUploadFields.length > 0 && (
                    <>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>
                          {dbUploadFields.length} field(s) detected — select to add as indexes:
                        </span>
                        <div style={{ display: "flex", gap: 10 }}>
                          <button type="button" className="link-btn" style={{ fontSize: 11 }}
                            onClick={() => setDbUploadSelected(new Set(dbUploadFields.map((f) => f.name)))}>
                            All
                          </button>
                          <button type="button" className="link-btn" style={{ fontSize: 11 }}
                            onClick={() => setDbUploadSelected(new Set())}>
                            None
                          </button>
                        </div>
                      </div>
                      <div className="createDbFieldList">
                        {dbUploadFields.map((f) => (
                          <div key={f.name} className="createDbFieldRow" style={{ alignItems: "center" }}>
                            <input
                              type="checkbox"
                              checked={dbUploadSelected.has(f.name)}
                              onChange={() => {
                                setDbUploadSelected((prev) => {
                                  const next = new Set(prev);
                                  next.has(f.name) ? next.delete(f.name) : next.add(f.name);
                                  return next;
                                });
                              }}
                              style={{ marginTop: 0, flexShrink: 0 }}
                            />
                            <input
                              type="text"
                              value={dbUploadRenames[f.name] ?? f.name}
                              onChange={(e) =>
                                setDbUploadRenames((prev) => ({ ...prev, [f.name]: e.target.value }))
                              }
                              disabled={!dbUploadSelected.has(f.name)}
                              style={{
                                flex: 1,
                                fontSize: 13,
                                fontWeight: 600,
                                border: "1px solid #d1d5db",
                                borderRadius: 5,
                                padding: "3px 8px",
                                marginLeft: 8,
                                background: dbUploadSelected.has(f.name) ? "#fff" : "#f3f4f6",
                                color: dbUploadSelected.has(f.name) ? "#111" : "#9ca3af",
                                minWidth: 0,
                              }}
                              title="Rename this field"
                            />
                            {f.sampleValues?.length > 0 && (
                              <span style={{ color: "#aaa", fontSize: 11, flexShrink: 0, marginLeft: 6 }}>
                                e.g. {f.sampleValues.slice(0, 2).join(", ")}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>

              ) : (
                /* ── Step 1: Upload form ── */
                <>
                  <div className="form-row">
                    <label>Files to upload</label>
                    <div className="muted" style={{ fontSize: 13, lineHeight: 1.6 }}>
                      {dbUploadFiles.map((f, i) => (
                        <div key={i}>
                          📄 {f.name}{" "}
                          <span style={{ color: "#888" }}>({Math.ceil(f.size / 1024)} KB)</span>
                          {f.name.toLowerCase().endsWith(".afp") && (
                            <span style={{ marginLeft: 6, color: "#1a6cbc", fontWeight: 600, fontSize: 11 }}>
                              AFP — will be queued for batch processing
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="form-row" style={{ marginTop: 10 }}>
                    <label>Destination</label>
                    <div className="db-mode-options">
                      <button
                        type="button"
                        className={`db-mode-option${dbUploadMode === "existing" ? " db-mode-option--active" : ""}`}
                        onClick={() => setDbUploadMode("existing")}
                      >
                        <span className="db-mode-option__dot" />
                        Existing database
                      </button>
                      <button
                        type="button"
                        className={`db-mode-option${dbUploadMode === "new" ? " db-mode-option--active" : ""}`}
                        onClick={() => setDbUploadMode("new")}
                      >
                        <span className="db-mode-option__dot" />
                        Create new database
                      </button>
                    </div>
                  </div>

                  {dbUploadMode === "existing" ? (
                    <div className="form-row">
                      <label>Select database</label>
                      <select
                        value={dbUploadExisting}
                        onChange={(e) => setDbUploadExisting(e.target.value)}
                      >
                        <option value="" disabled>— select a database —</option>
                        {dbUploadAllDbs.map((d) => (
                          <option key={d.id} value={d.dbName}>
                            {d.displayName}{d.fileExtension ? ` (${d.fileExtension})` : ""}
                          </option>
                        ))}
                      </select>
                      {dbUploadAllDbs.length === 0 && (
                        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                          No databases found. Create a new one.
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="form-row">
                        <label>New database name</label>
                        <input
                          autoFocus
                          type="text"
                          placeholder="e.g. AKBANK_STATEMENTS"
                          value={dbUploadName}
                          onChange={(e) => setDbUploadName(e.target.value.replace(/[^A-Za-z0-9_]/g, ""))}
                        />
                        <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                          Letters, digits and underscores only.
                        </div>
                      </div>

                      {/* Driver file — mirrors CreateDatabaseModal step 1 */}
                      <div className="form-row">
                        <label>
                          Driver file source{" "}
                          <span style={{ color: "#888", fontWeight: 400 }}>(optional)</span>
                        </label>

                        {/* Tab selector */}
                        <div className="createDbTabBar" style={{ marginTop: 6, marginBottom: 8 }}>
                          {DB_DRIVER_TABS.map((t) => (
                            <button
                              key={t.id}
                              type="button"
                              className={`createDbTab${dbUploadDriverTab === t.id ? " createDbTabActive" : ""}`}
                              onClick={() => { setDbUploadDriverTab(t.id); setDbUploadError(""); }}
                            >
                              {t.label}
                            </button>
                          ))}
                        </div>

                        {dbUploadDriverTab === "none" && (
                          <p className="createDbTabNote" style={{ margin: 0 }}>
                            No driver file — the database will be created with no custom index
                            columns. You can add indexes from the Admin panel at any time.
                          </p>
                        )}

                        {dbUploadDriverTab === "afp" && (
                          <div className="createDbFilePicker">
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              <button
                                type="button"
                                className="icon-btn"
                                style={{ fontSize: 12, padding: "5px 12px", whiteSpace: "nowrap" }}
                                onClick={() => driverAfpRef.current?.click()}
                              >
                                Choose AFP
                              </button>
                              <span className={`createDbFileName${dbUploadDriverAfp ? "" : " createDbFileNameEmpty"}`}>
                                {dbUploadDriverAfp ? dbUploadDriverAfp.name : "No file chosen"}
                              </span>
                              {dbUploadDriverAfp && (
                                <button type="button" className="link-btn" style={{ color: "#888" }}
                                  onClick={() => setDbUploadDriverAfp(null)}>×</button>
                              )}
                            </div>
                            <input ref={driverAfpRef} type="file" accept=".afp" style={{ display: "none" }}
                              onChange={(e) => { setDbUploadDriverAfp(e.target.files?.[0] || null); e.target.value = ""; }} />
                            <p className="createDbTabNote" style={{ marginTop: 6, marginBottom: 0 }}>
                              Tagged Logical Element (TLE) records will be scanned to suggest index fields.
                            </p>
                          </div>
                        )}

                        {dbUploadDriverTab === "meta" && (
                          <div className="createDbFilePicker">
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              <button
                                type="button"
                                className="icon-btn"
                                style={{ fontSize: 12, padding: "5px 12px", whiteSpace: "nowrap" }}
                                onClick={() => driverMetaRef.current?.click()}
                              >
                                Choose file
                              </button>
                              <span className={`createDbFileName${dbUploadDriverMeta ? "" : " createDbFileNameEmpty"}`}>
                                {dbUploadDriverMeta ? dbUploadDriverMeta.name : "No file chosen"}
                              </span>
                              {dbUploadDriverMeta && (
                                <button type="button" className="link-btn" style={{ color: "#888" }}
                                  onClick={() => setDbUploadDriverMeta(null)}>×</button>
                              )}
                            </div>
                            <input ref={driverMetaRef} type="file" accept=".json,.csv" style={{ display: "none" }}
                              onChange={(e) => { setDbUploadDriverMeta(e.target.files?.[0] || null); e.target.value = ""; }} />
                            <p className="createDbTabNote" style={{ marginTop: 6, marginBottom: 0 }}>
                              Column headers (CSV) or top-level keys (JSON) will be used as index field names.
                            </p>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {dbUploadError && <div className="error-text">{dbUploadError}</div>}
                </>
              )}
            </div>

            <div className="modal-actions">
              {dbUploadStep === "done" ? (
                <button className="primary-btn" onClick={() => setDbUploadModal(false)}>
                  Close
                </button>
              ) : dbUploadStep === 2 ? (
                <>
                  <button
                    type="button"
                    className="dbModalCancel"
                    onClick={handleDbUploadSkipIndexes}
                    disabled={dbUploadFieldBusy}
                  >
                    Skip
                  </button>
                  <button
                    type="button"
                    className="primary-btn"
                    disabled={dbUploadFieldBusy || dbUploadSelected.size === 0}
                    onClick={handleDbUploadAddIndexes}
                  >
                    {dbUploadFieldBusy
                      ? "Working…"
                      : `Add ${dbUploadSelected.size} Index(es) & Upload`}
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="primary-btn"
                    disabled={dbUploadBusy}
                    onClick={submitUploadToDatabase}
                  >
                    {dbUploadBusy
                      ? (dbUploadMode === "new" ? "Creating…" : "Uploading…")
                      : (dbUploadMode === "new" && dbUploadDriverTab !== "none"
                          ? "Create & Detect Fields →"
                          : "Upload")}
                  </button>
                  <button
                    className="mini-btn"
                    disabled={dbUploadBusy}
                    onClick={() => setDbUploadModal(false)}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Drop folder CRUD modal ── */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>

            {modalMode === "create" && (
              <>
                <div className="modal-title">
                  <span className="modal-title-icon">📁</span>
                  <div className="modal-title-content">
                    <div className="modal-title-text">New drop folder</div>
                    <div className="modal-title-sub">Configure an automated hot folder for a database</div>
                  </div>
                  <button className="drawer-close-btn" onClick={() => setModalOpen(false)} />
                </div>
                <div className="modal-body">
                  <div className="form-row">
                    <label>Database <span className="required-mark">*</span></label>
                    <select
                      value={form.dbInfoId}
                      onChange={(e) => setForm({ ...form, dbInfoId: e.target.value })}
                    >
                      <option value="" disabled>Select a database…</option>
                      {dbInfos.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-row">
                    <label>Drive</label>
                    <input
                      type="text"
                      placeholder="e.g. D  (leave blank on Linux/macOS)"
                      value={form.storageDrive}
                      onChange={(e) => setForm({ ...form, storageDrive: e.target.value })}
                    />
                  </div>
                  <div className="form-row">
                    <label>Folder path <span className="required-mark">*</span></label>
                    <div className="path-input-wrap">
                      <input
                        type="text"
                        placeholder="/path/to/drop  or  path\to\drop"
                        value={form.storageFolder}
                        onChange={(e) => setForm({ ...form, storageFolder: e.target.value })}
                      />
                      <button type="button" className="browse-btn" title="Browse folders" onClick={() => openPicker("storageFolder", form.storageFolder)}>📂</button>
                    </div>
                  </div>
                  <div className="advanced-section">
                    <div className="advanced-divider" />
                    <button
                      type="button"
                      className="advanced-toggle"
                      onClick={() => setAdvancedOpen(!advancedOpen)}
                    >
                      <span>⚙ Advanced settings</span>
                      <span className={`adv-chevron${advancedOpen ? " open" : ""}`}>▾</span>
                    </button>
                    {advancedOpen && (
                      <div className="advanced-body">
                        <div className="form-row">
                          <label>Process location</label>
                          <div className="path-input-wrap">
                            <input
                              type="text"
                              placeholder={t("upload.optionalLabel", { defaultValue: "optional" })}
                              value={form.processLocation}
                              onChange={(e) => setForm({ ...form, processLocation: e.target.value })}
                            />
                            <button type="button" className="browse-btn" title="Browse folders" onClick={() => openPicker("processLocation", form.processLocation)}>📂</button>
                          </div>
                        </div>
                        <div className="form-row">
                          <label>Error location</label>
                          <div className="path-input-wrap">
                            <input
                              type="text"
                              placeholder={t("upload.optionalLabel", { defaultValue: "optional" })}
                              value={form.errorLocation}
                              onChange={(e) => setForm({ ...form, errorLocation: e.target.value })}
                            />
                            <button type="button" className="browse-btn" title="Browse folders" onClick={() => openPicker("errorLocation", form.errorLocation)}>📂</button>
                          </div>
                        </div>
                        <div className="encoding-section">
                          <div className="form-row">
                            <label>Default encoding</label>
                            <div className="encoding-input-wrap">
                              <input
                                type="text"
                                placeholder="utf-8"
                                value={form.defaultEncoding}
                                onChange={(e) => setForm({ ...form, defaultEncoding: e.target.value })}
                              />
                              <button type="button" className="presets-btn" onClick={() => setEncodingPanelOpen(o => !o)}>
                                ≡ Presets
                              </button>
                            </div>
                          </div>
                          {encodingPanelOpen && (
                            <div className="encoding-panel">
                              <div className="encoding-panel-title">Common Encodings</div>
                              <div className="encoding-grid">
                                {ENCODING_PRESETS.map((p) => (
                                  <button key={p.value} type="button"
                                    className={`encoding-card${form.defaultEncoding === p.value ? " enc-selected" : ""}`}
                                    onClick={() => { setForm(f => ({ ...f, defaultEncoding: p.value })); setEncodingPanelOpen(false); }}
                                  >
                                    <div className="enc-name">{p.name}</div>
                                    <div className="enc-desc">{p.desc}</div>
                                    <div className="enc-value">{p.value}</div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {formError && <div className="error-text">{formError}</div>}
                </div>
                <div className="modal-actions">
                  <div className="modal-actions-copy-group">
                    <button
                      type="button"
                      className={`copy-settings-btn${copyFlash ? " copy-flash" : ""}`}
                      title="Copy all settings"
                      onClick={copyFolderSettings}
                    >
                      {copyFlash ? "✔ Copied" : "⧉ Copy settings"}
                    </button>
                    {copiedFolderSettings && (
                      <button
                        type="button"
                        className="paste-settings-btn"
                        title="Paste copied settings"
                        onClick={pasteFolderSettings}
                      >
                        ⊞ Paste settings
                      </button>
                    )}
                  </div>
                  <button
                    className="primary-btn"
                    disabled={busy}
                    onClick={async () => {
                      setFormError("");
                      if (!form.dbInfoId || !form.storageFolder) {
                        setFormError("DB and Folder path are required.");
                        return;
                      }
                      try {
                        setBusy(true);
                        const url = apiEndpoint("/api/Upload/DropFolders");
                        const resp = await fetch(url, {
                          method: "POST",
                          headers: {
                            ...(headers.Authorization ? { Authorization: headers.Authorization } : {}),
                            "Content-Type": "application/json",
                          },
                          body: JSON.stringify({
                            dbInfoId: Number(form.dbInfoId),
                            storageDrive: form.storageDrive || "",
                            storageFolder: form.storageFolder || "",
                            processLocation: form.processLocation || null,
                            errorLocation: form.errorLocation || null,
                            defaultEncoding: form.defaultEncoding || null,
                            packageSize: 100,
                          }),
                        });
                        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                        setModalOpen(false);
                        await loadDropFolders();
                      } catch {
                        setFormError("Failed to create drop folder.");
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    {busy ? "Creating…" : "Create"}
                  </button>
                  <button className="secondary-btn" onClick={() => setModalOpen(false)}>Cancel</button>
                </div>
              </>
            )}

            {modalMode === "edit" && editing && (
              <>
                <div className="modal-title">
                  <span className="modal-title-icon">✏️</span>
                  <div className="modal-title-content">
                    <div className="modal-title-text">Edit drop folder</div>
                    <div className="modal-title-sub">{editing.name || "Update the hot folder configuration"}</div>
                  </div>
                  <button className="drawer-close-btn" onClick={() => setModalOpen(false)} />
                </div>
                <div className="modal-body">
                  <div className="form-row">
                    <label>Database</label>
                    <select value={form.dbInfoId} disabled className="field-locked" onChange={() => {}}>
                      {dbInfos.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-row">
                    <label>Drive</label>
                    <input
                      type="text"
                      placeholder="e.g. D  (leave blank on Linux/macOS)"
                      value={form.storageDrive}
                      onChange={(e) => setForm({ ...form, storageDrive: e.target.value })}
                    />
                  </div>
                  <div className="form-row">
                    <label>Folder path</label>
                    <div className="path-input-wrap">
                      <input
                        type="text"
                        value={form.storageFolder}
                        onChange={(e) => setForm({ ...form, storageFolder: e.target.value })}
                      />
                      <button type="button" className="browse-btn" title="Browse folders" onClick={() => openPicker("storageFolder", form.storageFolder)}>📂</button>
                    </div>
                  </div>
                  <div className="advanced-section">
                    <div className="advanced-divider" />
                    <button
                      type="button"
                      className="advanced-toggle"
                      onClick={() => setAdvancedOpen(!advancedOpen)}
                    >
                      <span>⚙ Advanced settings</span>
                      <span className={`adv-chevron${advancedOpen ? " open" : ""}`}>▾</span>
                    </button>
                    {advancedOpen && (
                      <div className="advanced-body">
                        <div className="form-row">
                          <label>Process location</label>
                          <div className="path-input-wrap">
                            <input
                              type="text"
                              value={form.processLocation}
                              onChange={(e) => setForm({ ...form, processLocation: e.target.value })}
                            />
                            <button type="button" className="browse-btn" title="Browse folders" onClick={() => openPicker("processLocation", form.processLocation)}>📂</button>
                          </div>
                        </div>
                        <div className="form-row">
                          <label>Error location</label>
                          <div className="path-input-wrap">
                            <input
                              type="text"
                              value={form.errorLocation}
                              onChange={(e) => setForm({ ...form, errorLocation: e.target.value })}
                            />
                            <button type="button" className="browse-btn" title="Browse folders" onClick={() => openPicker("errorLocation", form.errorLocation)}>📂</button>
                          </div>
                        </div>
                        <div className="encoding-section">
                          <div className="form-row">
                            <label>Default encoding</label>
                            <div className="encoding-input-wrap">
                              <input
                                type="text"
                                placeholder="utf-8"
                                value={form.defaultEncoding}
                                onChange={(e) => setForm({ ...form, defaultEncoding: e.target.value })}
                              />
                              <button type="button" className="presets-btn" onClick={() => setEncodingPanelOpen(o => !o)}>
                                ≡ Presets
                              </button>
                            </div>
                          </div>
                          {encodingPanelOpen && (
                            <div className="encoding-panel">
                              <div className="encoding-panel-title">Common Encodings</div>
                              <div className="encoding-grid">
                                {ENCODING_PRESETS.map((p) => (
                                  <button key={p.value} type="button"
                                    className={`encoding-card${form.defaultEncoding === p.value ? " enc-selected" : ""}`}
                                    onClick={() => { setForm(f => ({ ...f, defaultEncoding: p.value })); setEncodingPanelOpen(false); }}
                                  >
                                    <div className="enc-name">{p.name}</div>
                                    <div className="enc-desc">{p.desc}</div>
                                    <div className="enc-value">{p.value}</div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {formError && <div className="error-text">{formError}</div>}
                </div>
                <div className="modal-actions">
                  <div className="modal-actions-copy-group">
                    <button
                      type="button"
                      className={`copy-settings-btn${copyFlash ? " copy-flash" : ""}`}
                      title="Copy all settings"
                      onClick={copyFolderSettings}
                    >
                      {copyFlash ? "✔ Copied" : "⧉ Copy settings"}
                    </button>
                    {copiedFolderSettings && (
                      <button
                        type="button"
                        className="paste-settings-btn"
                        title="Paste copied settings"
                        onClick={pasteFolderSettings}
                      >
                        ⊞ Paste settings
                      </button>
                    )}
                  </div>
                  <button
                    className="primary-btn"
                    disabled={busy}
                    onClick={async () => {
                      setFormError("");
                      try {
                        setBusy(true);
                        const url = apiEndpoint(`/api/Upload/DropFolders/${editing.id}`);
                        const resp = await fetch(url, {
                          method: "PUT",
                          headers: {
                            ...(headers.Authorization ? { Authorization: headers.Authorization } : {}),
                            "Content-Type": "application/json",
                          },
                          body: JSON.stringify({
                            storageDrive: form.storageDrive || "",
                            storageFolder: form.storageFolder || "",
                            processLocation: form.processLocation || null,
                            errorLocation: form.errorLocation || null,
                            defaultEncoding: form.defaultEncoding || null,
                            packageSize: 100,
                          }),
                        });
                        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                        setModalOpen(false);
                        await loadDropFolders();
                      } catch {
                        setFormError("Failed to update drop folder.");
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    {busy ? "Saving…" : "Save changes"}
                  </button>
                  <button className="secondary-btn" onClick={() => setModalOpen(false)}>Cancel</button>
                </div>
              </>
            )}

            {modalMode === "delete" && editing && (
              <>
                <div className="modal-title modal-title-danger">
                  <span className="modal-title-icon">🗑️</span>
                  <div className="modal-title-content">
                    <div className="modal-title-text">Remove drop folder</div>
                    <div className="modal-title-sub">This action cannot be undone</div>
                  </div>
                  <button className="drawer-close-btn" onClick={() => setModalOpen(false)} />
                </div>
                <div className="modal-body">
                  <div className="delete-confirm-card">
                    <div className="delete-confirm-name">{editing.name}</div>
                    {editing.path && <div className="delete-confirm-path">{formatPath(editing.path)}</div>}
                  </div>
                  <div className="muted" style={{ fontSize: 13 }}>
                    This will remove the automation mapping. Files on disk are not deleted.
                  </div>
                  {formError && <div className="error-text">{formError}</div>}
                </div>
                <div className="modal-actions">
                  <button
                    className="primary-btn btn-danger"
                    disabled={busy}
                    onClick={async () => {
                      setFormError("");
                      try {
                        setBusy(true);
                        const url = apiEndpoint(`/api/Upload/DropFolders/${editing.id}`);
                        const resp = await fetch(url, {
                          method: "DELETE",
                          headers: {
                            ...(headers.Authorization ? { Authorization: headers.Authorization } : {}),
                          },
                        });
                        if (resp.status !== 204 && !resp.ok) throw new Error(`HTTP ${resp.status}`);
                        setModalOpen(false);
                        await loadDropFolders();
                      } catch {
                        setFormError("Failed to delete drop folder.");
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    {busy ? "Removing…" : "Remove"}
                  </button>
                  <button className="secondary-btn" onClick={() => setModalOpen(false)}>Cancel</button>
                </div>
              </>
            )}

          </div>
        </div>
      )}

      {/* ── Folder picker modal ── */}
      {picker.open && (
        <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={() => setPicker(p => ({ ...p, open: false }))}>
          <div className="modal fp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">
              <span className="modal-title-icon">📂</span>
              <div className="modal-title-content">
                <div className="modal-title-text">Browse folders</div>
                <div className="modal-title-sub fp-path-crumb" title={picker.path}>{picker.path || "/"}</div>
              </div>
              <button className="drawer-close-btn" onClick={() => setPicker(p => ({ ...p, open: false }))} />
            </div>
            <div className="fp-body">
              {picker.loading && <div className="fp-status">Loading…</div>}
              {picker.error && <div className="fp-status fp-error">{picker.error}</div>}
              {!picker.loading && !picker.error && (
                <div className="fp-list">
                  {picker.parent != null && (
                    <button className="fp-entry fp-up" onClick={() => navigatePicker(picker.parent, false)}>
                      <span className="fp-icon">↑</span>
                      <span className="fp-name">..</span>
                    </button>
                  )}
                  {picker.entries.length === 0 && (
                    <div className="fp-status fp-muted">No subfolders</div>
                  )}
                  {picker.entries.map((e) => (
                    <button key={e.path} className="fp-entry" onClick={() => navigatePicker(e.path)}>
                      <span className="fp-icon">📁</span>
                      <span className="fp-name">{e.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button
                className="primary-btn"
                disabled={!picker.path}
                onClick={() => {
                  setForm(f => ({ ...f, [picker.target]: picker.path }));
                  setPicker(p => ({ ...p, open: false }));
                }}
              >
                Select this folder
              </button>
              <button className="secondary-btn" onClick={() => setPicker(p => ({ ...p, open: false }))}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
